import React from 'react';
import moment from 'moment-timezone';
import {
  takeEvery,
  put,
  all,
  select,
  spawn,
  takeLatest
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { actions as toastrActions } from 'react-redux-toastr';

import * as types from '../actions/auth-user/types';
import * as actions from '../actions/auth-user';
import { setSignInEmail } from '../actions/user-sign-in';

import UpdateAccountSettingLink from '../components/common/update-account-setting-link';

import httpStatusCodes from '../constants/http-status-codes';

import { parseHttpStatusText, getRedirectAfterSignInPath } from '../tools';
import history from '../tools/history';
import apiFetcher from '../tools/api-fetcher';
import jwtDecode from '../tools/jwt-decode';
import handleError from '../tools/error-helper';
import * as tokenStorage from '../tools/token-storage';

import { actions as idleActions } from '../tools/idle-monitor';

import config from '../config';

import { getInactivityStatus } from '../actions/idle-monitor';
import { IDLESTATUS_AWAY } from '../constants/idle-monitor-statuses';

const LOG_AUTH_SAGAS = false;

function* init() {
  const sharedAuthToken = tokenStorage.getSharedToken();

  yield initUserBySharedAuthToken(sharedAuthToken);

  const stateAuthUser = yield select(state => state.authUser);

  if (!stateAuthUser.get('userId')) {
    const nativeAuthToken = tokenStorage.getNativeToken();
    yield initUserByNativeAuthToken(nativeAuthToken);
  }

  yield spawn(watchAuthTokens);
}

function* initUserBySharedAuthToken(sharedAuthToken) {
  if (!sharedAuthToken) {
    return;
  }

  yield put(actions.setLoading(true));

  try {
    const { data } = yield apiFetcher.post('auth-tokens/from-sso', {
      sharedAuthToken
    });

    yield setUserFromAuthData(data);

    yield checkLocalAndUserTimeZoneDifference();
  } catch (exception) {
    if (
      exception.response &&
      exception.response.status === httpStatusCodes.Unauthorized
    ) {
      tokenStorage.clearSharedToken();
    } else {
      handleError(exception);
    }
  }

  yield put(actions.setLoading(false));
}

function* initUserByNativeAuthToken(nativeAuthToken) {
  if (!nativeAuthToken) {
    return;
  }

  yield put(actions.setLoading(true));

  try {
    const { data } = yield apiFetcher
      .auth(nativeAuthToken)
      .get('auth-tokens/next');

    yield setUserFromAuthData(data);

    yield checkLocalAndUserTimeZoneDifference();
  } catch (exception) {
    if (
      exception.response &&
      exception.response.status === httpStatusCodes.Unauthorized
    ) {
      tokenStorage.clearNativeToken();
    } else {
      handleError(exception);
    }
  }

  yield put(actions.setLoading(false));
}

function* signIn({ email, password, redirect }) {
  yield put(actions.setLoading(true));
  yield put(setSignInEmail(email));

  let authData = null;

  try {
    const { data } = yield apiFetcher.post('auth-tokens', {
      email,
      password
    });

    authData = data;
  } catch (error) {
    if (error.response) {
      const { status, statusText } = error.response;

      if (status === 401) {
        const parsedStatusText = parseHttpStatusText(statusText);

        const { text, subcode } = parsedStatusText;

        yield put(actions.setError({ text, subcode }));

        if (redirect) {
          history.replace('/sign-in');
        }
      }
    } else {
      handleError(error);
    }
  }

  if (authData) {
    try {
      yield setUserFromAuthData(authData);

      const stateAuthUser = yield select(state => state.authUser);
      const user = stateAuthUser.toJS();
      
      if (redirect) {
        const redirectPath = yield getRedirectAfterSignInPath(user.userType);
        history.push(redirectPath);
      }

      yield checkLocalAndUserTimeZoneDifference(
        user.accountSettings.ianaTimeZone
      );
    } catch (exception) {
      handleError(exception);
    }
  }

  yield put(actions.setLoading(false));
}

function* impersonate({ userId }) {
  yield put(actions.setLoading(true));

  let authData = null;

  try {
    const { data } = yield apiFetcher
      .auth()
      .post('auth-tokens/impersonated', { userId });

    authData = data;
  } catch (error) {
    handleError(error, 'Failed to impersonate.');
  }

  if (authData) {
    try {
      yield setUserFromAuthData(authData);
    } catch (exception) {
      handleError(exception);
    }

    const authUser = yield select(state => state.authUser);
    const redirectPath = yield getRedirectAfterSignInPath(
      authUser.get('userType')
    );

    history.push(redirectPath);
  }

  yield put(actions.setLoading(false));
}

function* signOut() {
  yield hideTimezoneMismatchWarning();

  tokenStorage.clearNativeToken();
  tokenStorage.clearSharedToken();

  yield put(idleActions.stop());

  yield put(actions.resetUser());
  yield put(actions.setSignedOutExplicitly());

  localStorage.removeItem('auth-data');
  localStorage.removeItem('inactivity-status')
}

function* setUserFromAuthData({ nativeAuthToken, sharedAuthToken }) {
  const nativeTokenExpires = tokenStorage.setNativeToken(nativeAuthToken);
  const sharedTokenExpires = tokenStorage.setSharedToken(sharedAuthToken);

  setAuthData({ 
    nativeAuthToken: {
      token: nativeAuthToken,
      expires: nativeTokenExpires
    },
    sharedAuthToken: {
      token: sharedAuthToken,
      expires: sharedTokenExpires
    }
  });

  const user = yield jwtDecode(nativeAuthToken);

  yield put(
    actions.setUserData({
      user,
      nativeAuthToken,
      sharedAuthToken
    })
  );

  yield put(idleActions.start());
}

function* checkLocalAndUserTimeZoneDifference() {
  const stateAuthUser = yield select(state => state.authUser);
  const user = stateAuthUser.toJS();
  const userTimeZone = user.accountSettings.ianaTimeZone;

  const localTimeZone = moment.tz.guess();

  if (userTimeZone !== localTimeZone) {
    yield showTimezoneMismatchWarning(userTimeZone);
  }
}

const timezoneMismatchWarningToastrId = 'tzMismatchWarningToastr';

function* showTimezoneMismatchWarning(userTimeZone) {
  yield put(
    toastrActions.add({
      id: timezoneMismatchWarningToastrId,
      type: 'warning',
      title: `Your computer's time zone does not appear to match your account time zone preference of ${userTimeZone}.`,
      position: 'top-center',
      options: {
        timeOut: 0,
        showCloseButton: true,
        component: <UpdateAccountSettingLink />
      }
    })
  );
}

function* hideTimezoneMismatchWarning() {
  yield put(toastrActions.remove(timezoneMismatchWarningToastrId));
}

function* watchAuthTokens() {
  const pollIntervalInMilliseconds = config.syncAuthTokensDelayInMilliseconds;

  log(`Started watching Auth Tokens each ${pollIntervalInMilliseconds}ms...`);

  while (true) {
    yield delay(pollIntervalInMilliseconds);

    log('Triggering sync action...');
    yield put(actions.syncWithAuthTokens());
  }
}

function* syncWithAuthTokens() {
  log('\n*********\nStarted sync with Auth Tokens...');

  const authUser = yield select(state => state.authUser);

  if (authUser.get('isLoading')) {
    log('Auth User is loading now. Further procession will be skipped.');
    return;
  }

  const syncedWithSharedAuthToken = yield syncWithSharedAuthToken();

  if (syncedWithSharedAuthToken) {
    log(
      'Auth User has been synced with shared token. Further procession will be skipped.'
    );
    return;
  }

  const nativeAuthToken = tokenStorage.getNativeToken();

  log(`Syncing with native auth token [${nativeAuthToken}]`);

  if (!nativeAuthToken) {
    log(`Native auth token is empty.`);

    if (authUser.get('userId')) {
      log(`Logged user ${authUser.get('userId')} will be signed out.`);
      yield put(actions.signOut());
    }

    log('Sync by native token is completed.');
    return;
  }
  else {
    const { nativeAuthToken } = getAuthData();
    const refreshTokenBeforeDate = moment().add(config.refreshTokenBefore, 'millisecond').toDate();
    const userInactivityStatus = getInactivityStatus();

    if (new Date(nativeAuthToken.expires) < refreshTokenBeforeDate && (!userInactivityStatus || userInactivityStatus === IDLESTATUS_AWAY)) {
      log(`Refreshing the token for active user ${authUser.get('userId')}`);

      apiFetcher.refreshAuthTokens();
    }
  }

  if (!authUser.get('userId')) {
    log('No logged user. Will try to restore it by native auth token.');
    yield initUserByNativeAuthToken(nativeAuthToken);

    log('Sync by native token is completed.');
    return;
  }

  const currentNativeAuthToken = authUser.get('nativeAuthToken');

  if (currentNativeAuthToken === nativeAuthToken) {
    log(
      'Current and saved native tokens are the same.\nSync by native token is completed.'
    );
    return;
  }

  log('Current and saved native tokens are different.');

  const user = yield jwtDecode(nativeAuthToken);

  if (authUser.get('userId') === user.userId) {
    log(
      "Native token refers to the currently logged user. We'll update data for the current user from the native token."
    );
    yield put(
      actions.setUserData({
        user,
        nativeAuthToken,
        sharedAuthToken: authUser.get('sharedAuthToken')
      })
    );

    log('Sync by native token is completed.');
    return;
  }

  log(
    `Native token refers to user ${user.userId} which is different from current user ${authUser.get(
      'userId'
    )}. Current user will be singed out.`
    );

  yield put(actions.signOut());
  yield initUserByNativeAuthToken(nativeAuthToken);

  log('Sync by native token is completed.');
}

function* syncWithSharedAuthToken() {
  const sharedAuthToken = tokenStorage.getSharedToken();

  log(`Syncing with shared auth token [${sharedAuthToken}]`);

  if (!sharedAuthToken) {
    log(`Shared token is empty. No need to sync up.`);
    // do not check for missing shared token because in case of Sign out on SA side native token will be also removed
    // or the user may be not an SA candidate
    return false;
  }

  const authUser = yield select(state => state.authUser);

  if (!authUser.get('userId')) {
    log('No logged user. Try to restore user from the provided shared token.');
    yield initUserBySharedAuthToken(sharedAuthToken);
    log('Sync by shared token is completed.');
    return true;
  }

  const currentSharedAuthToken = authUser.get('sharedAuthToken');

  if (!currentSharedAuthToken) {
    log(
      'Currently logged user does not have shared token and thus is not an SA candidate. Will be signed out and restored from shared token.'
    );
    yield put(actions.signOut());

    yield initUserBySharedAuthToken(sharedAuthToken);
    log('Sync by shared token is completed.');
    return true;
  }

  if (sharedAuthToken === currentSharedAuthToken) {
    log(
      'Current and saved shared tokens are the same.\nSync by shared token is completed.'
    );
    return false;
  }

  const currentSearchAssociatesUser = jwtDecode(currentSharedAuthToken);
  const searchAssociatesUser = jwtDecode(sharedAuthToken);

  const searchAssociatesUserIsTheSame =
    currentSearchAssociatesUser.searchAssociatesUserId ===
      searchAssociatesUser.searchAssociatesUserId &&
    currentSearchAssociatesUser.searchAssociatesUserType ===
      searchAssociatesUser.searchAssociatesUserType;

  if (searchAssociatesUserIsTheSame) {
    log(
      `Current and saved shared token refer to the same SA user ${searchAssociatesUser.searchAssociatesUserId /
        searchAssociatesUser.searchAssociatesUserType}.\nSync by shared token is completed.`
    );
    // ToDO: possibly check for expiration
    return false;
  }

  log(
    `Current SA user ${currentSearchAssociatesUser.searchAssociatesUserId /
      currentSearchAssociatesUser.searchAssociatesUserType} does not match SA user from saved shared token ${searchAssociatesUser.searchAssociatesUserId /
      searchAssociatesUser.searchAssociatesUserType} and will be signed out and replaced.`
  );

  yield put(actions.signOut());
  initUserBySharedAuthToken(sharedAuthToken);

  log('Sync by shared token is completed.');
  return true;
}

function setAuthData(authData) {
  localStorage.setItem('auth-data', JSON.stringify(authData))
}

function getAuthData() {
  const authData = localStorage.getItem('auth-data')

  if (!authData) {
    return null;
  }

  return JSON.parse(authData);
}

function log() {
  if (!config.debug || !LOG_AUTH_SAGAS) {
    return;
  }

  console.log.apply(console, arguments);
}

export default function* userSaga() {
  yield all([
    takeEvery(types.AUTH_USER_INIT, init),
    takeEvery(types.AUTH_USER_SIGN_IN, signIn),
    takeEvery(types.AUTH_USER_SIGN_OUT, signOut),
    takeEvery(types.AUTH_USER_SET_USER_FROM_AUTH_DATA, setUserFromAuthData),
    takeEvery(types.AUTH_USER_IMPERSONATE, impersonate),
    takeLatest(types.AUTH_USER_SYNC_WITH_AUTH_TOKENS, syncWithAuthTokens)
  ]);
}



// WEBPACK FOOTER //
// ./src/sagas/auth-user.js